import {
  Empty,
  Pagination,
  PaginationProps,
  Select,
  SelectProps,
  Spin,
  Table,
  TableColumnsType,
  Tag,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilterFrame } from "../../components/Application/FilterFrame/FilterFrame";
import { selectViewShowBy } from "../../constants/selectViewShowBy";
import { IFilterApplicationForm } from "../../interfaces/Application/FilterFrame/IFilterApplicationForm";
import { IResponseApplicationsParams } from "../../interfaces/Application/IResponseApplicationsParams/IResponseApplicationsParams";
import { ITableApplication } from "../../interfaces/ItableApplication";
import { IFilterFrameProps } from "../../interfaces/props/IFilterFrameProps";
import { useLazyGetFilteredListApplicationQuery } from "../../store/api/application.api";
import { IApplications } from "../../store/slice/authSliceLogin/authSliceLogin";
import { getSearchParamFromURL } from "../../utils/utilities/getSearchParamFromUrl";
import { withLoadingHidePagination } from "./helpers/withLoadingHidePagination";
import { formatPrice } from "../../utils/utilities/formatPrice";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import { SELECT_APPLICATIONS, SELECT_APPLICATIONS_LAST_PAGE } from "../../store/selectors/applicatoinsSelector";
import { useSelector } from "react-redux";
import { UP_DATE_APPLICATIONS_ALL } from "../../store/slice/applicationsSlice/applicationsSlice";

import classes from "./PageApplications.module.css";

/**
 * Компонент страницы списка заявок
 */
export function PageApplications(): ReactElement {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [pageNumber, setPageNumber] = useState(1);
  const permissionApplications = useAppSelector(SELECT_APPLICATIONS);
  const lastPage = useSelector(SELECT_APPLICATIONS_LAST_PAGE)
  const [isHidePagination, setIsHidePagination] = useState(false);
  const [limit, setLimit] = useState(selectViewShowBy[0].value);
  const [searchParams] = useSearchParams();

  const [trigger, { data: dataApplications, isFetching }] =
    useLazyGetFilteredListApplicationQuery();

  const filterFormState = useRef<IFilterApplicationForm>({});

  useEffect(() => {
    if (permissionApplications && permissionApplications.length) {
      return;
    }

    const status_preset = searchParams.get("status_preset") || undefined;
    setPageNumber(1);
    withLoadingHidePagination(setIsHidePagination, () =>
      trigger(
        {
          page: 1,
          limit,
          status_preset,
          ...filterFormState.current,
        },
        true,
      ).unwrap(),
    );
  }, [searchParams]);

  const filteredApplications = permissionApplications.length ? permissionApplications : dataApplications?.applications.data;
  const totalPages = permissionApplications.length ? lastPage : dataApplications?.applications.last_page;

  const onHandleSelect: SelectProps["onChange"] = async (value) => {
    setLimit(value);
    setPageNumber(1);

    const status_preset = getSearchParamFromURL("status_preset") || undefined;

    withLoadingHidePagination(setIsHidePagination, () => {
      return trigger({
        page: 1,
        limit: value,
        status_preset,
        ...filterFormState.current,
      }).unwrap();
    });
  };

  const debouncedSearch = useCallback(
    debounce(async (value: Omit<IResponseApplicationsParams, "page">) => {
      setPageNumber(1);
      const status_preset = getSearchParamFromURL("status_preset");
      withLoadingHidePagination(setIsHidePagination, () => {
        return trigger(
          {
            ...value,
            page: 1,
            status_preset: status_preset || undefined,
          },
          true,
        ).unwrap();
      });
    }, 500),
    [searchParams],
  );

  const onHandleFilterForm: IFilterFrameProps["onChangeForm"] = (value) => {
    filterFormState.current = value;
    debouncedSearch({ ...value, limit });
  };

  const onHandleResetForm: IFilterFrameProps["onResetForm"] = async () => {
    setPageNumber(1);
    filterFormState.current = {};
    dispatch(UP_DATE_APPLICATIONS_ALL({ data: [], last_page: 1 }));
    const status_preset = searchParams.get("status_preset");

    withLoadingHidePagination(setIsHidePagination, () =>
      trigger(
        {
          page: 1,
          limit: limit,
          status_preset: status_preset || undefined,
        },
        true,
      ).unwrap(),
    );
  };

  const onHandleChangePagination: PaginationProps["onChange"] = (
    page: number,
  ) => {
    const status_preset = searchParams.get("status_preset");
    dispatch(UP_DATE_APPLICATIONS_ALL({ data: [], last_page: 1 }));
    setPageNumber(page);
    trigger(
      {
        page: page,
        limit,
        status_preset: status_preset || undefined,
      },
      true,
    );
  };

  const columnsApplication: TableColumnsType<ITableApplication> = [
    {
      title: "№ Заявки",
      dataIndex: "id",
      render: (id) => <span style={{ color: "#3c79ff" }}>№{id}</span>,
      width: 130,
    },
    {
      title: "Статус заявки",
      dataIndex: "status",
      render: (status) => (
        <Tag className={classes.tag} color={status?.color}>
          {status?.title}
        </Tag>
      ),
      width: 170,
      minWidth: 160,
    },
    {
      title: "Клиент",
      dataIndex: "organizationName",
      width: 300,
    },
    {
      title: "ИНН",
      dataIndex: "organizationInn",
      width: 95,
    },
    {
      title: "Сумма БГ",
      dataIndex: "applicationSumBg",
      width: 120,
      render: (value) => {
        return <>{formatPrice(value)}</>;
      },
    },
    {
      title: "Срок",
      dataIndex: "term",
      width: 40,
    },
    {
      title: "Тип заявки",
      dataIndex: "bgTypeName",
      width: 120,
    },
    {
      title: "ФЗ",
      dataIndex: "purchaseFz",
      width: 80,
      render: (fz) => {
        return <>{fz} ФЗ</>;
      },
    },
    {
      title: "Номер закупки",
      dataIndex: "purchaseNumber",
      width: 140,
    },
  ];

  const dataTableApplicationSource: ITableApplication[] | undefined =
    filteredApplications?.map((item: IApplications) => {
      const {
        id,
        status,
        organization,
        application_sum_bg,
        bg_type,
        purchase,
        start_bg_at,
        finish_bg_at,
      } = item;

      const termValue = dayjs(finish_bg_at).diff(dayjs(start_bg_at), "day");

      return {
        key: id,
        id: id,
        status: status,
        organizationName: organization.name,
        organizationInn: organization.inn,
        applicationSumBg: application_sum_bg,
        term: termValue,
        bgTypeName: bg_type.name,
        purchaseFz: purchase.fz,
        purchaseNumber: purchase.purchase_number,
      };
    });

  const ConstructorJsxApplications =
    filteredApplications?.length !== 0 ? (
      <Table
        className={classes.table}
        columns={columnsApplication}
        rowClassName={classes.row}
        locale={{ emptyText: false }}
        onRow={(data) => {
          return {
            onClick: () => {
              navigate(`/application/${data.id}`);
            },
          };
        }}
        dataSource={dataTableApplicationSource}
        pagination={false}
        loading={{ spinning: isFetching, size: "large" }}
        size="small"
      />
    ) : (
      <Spin size="large" spinning={isFetching} />
    );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <FilterFrame
          onChangeForm={onHandleFilterForm}
          onResetForm={onHandleResetForm}
        />
      </div>
      <div className={classes.bankCardsWrapper}>
        {ConstructorJsxApplications}
      </div>

      <div className={classes.paginationContainer}>
        <div>
          Показывать по:{" "}
          <Select
            onChange={onHandleSelect}
            value={limit}
            options={selectViewShowBy}
            className={classes.paginationSelect}
          />
        </div>

        {totalPages && !isHidePagination ? (
          <Pagination
            className={classes.pagination}
            total={totalPages}
            current={pageNumber}
            onChange={onHandleChangePagination}
            defaultPageSize={1}
          />
        ) : null}
      </div>
    </div>
  );
}
